/* eslint-disable react/prop-types */
import { useState, useEffect, useCallback, useRef } from "react";
import NavMenu from "./MaterialUI/NavMenu";
import ShareView from "./MaterialUI/ShareView";
import GeminiComponent from "./GeminiAPI/GeminiComponent";
import ExportModal from "./modals/ExportModal";
import "./NavBar.css";

// Import Firestore functions and authentication
import { db, auth, realtime } from "../../firebaseConfig";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { ref, set, onValue } from "firebase/database";
import RecentViewSkeleton from "./recents/RecentViewSkeleton";

import { useTour } from "@reactour/tour";
import { tickets } from "@mergeapi/merge-node-client/api/resources/ticketing";

function NavBar({
  onSearch,
  onSendData,
  onTableDataParsed,
  onCreateVisualization,
  onSlideCreated,
  viewId, // Accept viewId as a prop
  pageTitle = "", // Optional static title prop
  onPresentationModeChange,
  exportData,
  viewData,
  slideData,
  userID,
  components,
  tableToolbarRef,
  disableRemoteSync = false,
  // Add the Google Auth props here
  // isUserGoogleSignedIn,
  onRequestGoogleSignIn,
  createGoogleDocApiCall,
  isExporting,
  setIsExporting,
}) {
  const exportImages = () => {
    // Implementation for exporting images
  };

  const [editableTitle, setEditableTitle] = useState(pageTitle || "");
  const [isPresentationMode, setIsPresentationMode] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const authUser = userID;
  const [navBarStyle, setNavBarStyle] = useState({ transform: "translateY(-100%)", transition: "transform 0.5s ease" });
  const [buttonStyle, setButtonStyle] = useState({ transform: "none", transition: "transform 0.5s ease" });
  const initialSyncRef = useRef(true);

  // Function to fetch title from Firestore
  const fetchTitleFromFirestore = useCallback(async () => {
    if (!viewId) return;

    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.error("No authenticated user found.");
      return;
    }

    const docRef = doc(db, "views", viewId);
    try {
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setEditableTitle(data.title || ""); // Set title or empty string if undefined
      } else {
        console.log("No such document!");
      }
    } catch (error) {
      console.error("Error fetching title from Firestore:", error);
    }
  }, [viewId]);

  // Function to update title in Firestore
  const updateTitleInFirestore = async (newTitle) => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.error("No authenticated user found.");
      return;
    }

    const docRef = doc(db, "views", viewId);

    try {
      await updateDoc(docRef, { title: newTitle });
      //console.log("Title updated in Firestore.");
    } catch (error){
      console.error("Error updating title in Firestore:", error);
    }
  }

  const handleTitleChange = (event) => {
    const newTitle = event.target.value;
    setEditableTitle(newTitle);
    updateTitleInFirestore(newTitle);
  };

  // Fetch the title when the component mounts (for dynamic pages)
  useEffect(() => {
    if (disableRemoteSync || pageTitle || !viewId) {
      return;
    }
    fetchTitleFromFirestore();
  }, [disableRemoteSync, fetchTitleFromFirestore, pageTitle, viewId]);

  // Presentation mode
  useEffect(() => {
    if (disableRemoteSync || !viewId) {
      return;
    }

    const presentationModeRef = ref(realtime, `collabLayer/views/${viewId}/settings/presentationMode`);
    const unsubscribe = onValue(presentationModeRef, (snapshot) => {
      const data = snapshot.val();
      const mode = data === null ? false : data;
      setIsPresentationMode(mode);
      onPresentationModeChange(mode);
    });

    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [disableRemoteSync, onPresentationModeChange, viewId]);

  const updatePresentationMode = useCallback(
    (presentationMode) => {
      set(ref(realtime, `collabLayer/views/${viewId}/settings/`), {
        presentationMode,
      });
      onPresentationModeChange(presentationMode);
    },
    [onPresentationModeChange, viewId]
  );

  const handlePresentationModeChange = useCallback(() => {
    setIsPresentationMode(!isPresentationMode);
    updatePresentationMode(!isPresentationMode);
  }, [isPresentationMode, updatePresentationMode]);

  useEffect(() => {
    const handlePresentationModeKeyCommand = (event) => {
      if (event.metaKey && event.key === "j") {
        event.preventDefault();
        handlePresentationModeChange();
      }
    };
    document.addEventListener("keydown", handlePresentationModeKeyCommand);
    return () => {
      document.removeEventListener("keydown", handlePresentationModeKeyCommand);
    };
  }, [handlePresentationModeChange, isPresentationMode]);

  const handlePresentationMode = (e) => {
    if (e) e.preventDefault();
    // Toggle presentation mode
    console.log("Presentation mode toggled.");
    handlePresentationModeChange();
  };

  useEffect(() => {
    const duration = 500;

    if (initialSyncRef.current) {
      initialSyncRef.current = false;
      if (isPresentationMode) {
        setNavBarStyle({ transform: "translate(0px, -120px)", transition: "none" });
        setButtonStyle({ transform: "translate(0px, 120px)", transition: "none" });

        // Restore transition after initial render to enable future animations
        setTimeout(() => {
          setNavBarStyle({ transform: "translate(0px, -120px)", transition: `transform ${duration}ms ease` });
          setButtonStyle({ transform: "translate(0px, 120px)", transition: `transform ${duration}ms ease` });
        }, 100);
      } else {
        setNavBarStyle({ transform: "none", transition: "transform 0.5s ease" });
        setButtonStyle({ transform: "none", transition: "transform 0.5s ease" });
      }
      return;
    }

    if (isPresentationMode) {
      // Sequence: Y then X
      setNavBarStyle({ transform: "translate(0px, -120px)", transition: `transform ${duration}ms ease` });
      setButtonStyle({ transform: "translate(0px, 120px)", transition: `transform ${duration}ms ease` });

      const timer = setTimeout(() => {
        setNavBarStyle({ transform: "translate(0px, -120px)", transition: `transform ${duration}ms ease` });
        setButtonStyle({ transform: "translate(144px, 120px)", transition: `transform ${duration}ms ease` });
      }, duration);

      return () => clearTimeout(timer);
    } else {
      // Sequence: X then Y
      setNavBarStyle({ transform: "translate(0px, -120px)", transition: `transform ${duration}ms ease` });
      setButtonStyle({ transform: "translate(0px, 120px)", transition: `transform ${duration}ms ease` });

      const timer = setTimeout(() => {
        setNavBarStyle({ transform: "none", transition: `transform ${duration}ms ease` });
        setButtonStyle({ transform: "none", transition: `transform ${duration}ms ease` });
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [isPresentationMode]);

    const { setIsOpen } = useTour();

    const geminiComponentRef = useRef(null);

  

    const exportToDocs = () => {

      setIsExportModalOpen(true);

    };

  

    const handleExport = async (selectedSlides, exportType) => {
      setIsExporting(true);
      try {
        if (geminiComponentRef.current) {
          if (exportType === "Google Docs") {
            await geminiComponentRef.current.exportToGDocs(selectedSlides);
          } else if (exportType === "Google Slides") {
            await geminiComponentRef.current.exportToGSlides(selectedSlides);
          } else if (exportType === "Confluence page") {
            // console.log("Confluence page")
            await geminiComponentRef.current.exportToConfluence(selectedSlides);
          } else if (exportType === "Hubspot Tickets") {
            await geminiComponentRef.current.exportToHubspot(selectedSlides);
          } else if (exportType === "JIRA tickets") {
            await geminiComponentRef.current.exportToJira(selectedSlides);
          } else if (exportType === "Linear Tickets") {
            await geminiComponentRef.current.exportToLinear(selectedSlides);
          }
        }
      } finally {
        setIsExporting(false);
      }
    };

  

    return (

      <>

        <nav

          className={`navbar${isPresentationMode ? " presentation-mode" : ""}`}

          style={navBarStyle}

        >

          <NavMenu alt="Main menu" title="Main menu" />

          

          {pageTitle ? ( // Conditionally render static title if prop is provided

            <div className="pageTitle" data-testid="static-page-title">

              {pageTitle}

            </div>

          ) : (

            <div className="pageTitleInput">

              <input type="text" placeholder="My New Project" value={editableTitle} onChange={handleTitleChange} />

            </div>

          )}

  

          {/* AI component*/}

          <div className="ai-chat" maxHeight="90px">

            <GeminiComponent

              ref={geminiComponentRef}

              onSearch={onSearch}

              onSendData={onSendData}

              onTableDataParsed={onTableDataParsed}

              onCreateVisualization={onCreateVisualization}

              showAnalyzeButton={true}

              authUser={authUser}

              viewData={viewData}

              onSlideCreated={onSlideCreated}

              exportData={exportData}

              setComponents={components}

              tableToolbarRefs={tableToolbarRef}

              // Pass Google Auth related props down to GeminiComponent

                            // isUserGoogleSignedIn={isUserGoogleSignedIn}

                            onRequestGoogleSignIn={onRequestGoogleSignIn}

                            createGoogleDocApiCall={createGoogleDocApiCall}

                            setIsExporting={setIsExporting}

                          />

          </div>

          {/* {console.log("auth.currentUser :", auth.currentUser.uid)} */}

          <div style={{ width: "5%" }}></div>

  

        <div className="ai-chat">

  

            {viewId ? (

              <div

                className={`navicon-button${isPresentationMode ? " active" : ""}`}

                onClick={handlePresentationMode}

                style={buttonStyle}

                alt="Presentation Mode Toggle"

                title="Toggle Presentation Mode (Cmd + J)"

              >

                <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">

                  <svg x="8" y="6" width="25" height="28" viewBox="0 0 25 25">

                    <rect x="2.5" y="2.5" width="20" height="16" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none" />

                    <path d="M11 8v4l3.5-2z" fill="currentColor" transform="scale(1.8)" transform-origin="12.2 10" />

                    <rect x="12" y="18" width="1" height="3" fill="currentColor" />

                    <circle cx="12.5" cy="23.5" r="2.5" fill="currentColor" />

                  </svg>

                </svg>

              </div>

            ) : null}

        </div>

  

  

        <div className="ai-chat">

  

          {viewId ? (

            <button className="navicon-button" onClick={exportToDocs} atl="Export to Google Docs and Slides" title="Export to Google Docs and Slides">

              <svg xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="-5.0 -18.0 110.0 135.0" fill="var(--Grey6)">

              <path d="m37.5 30.207c-17.805 0-32.293 14.484-32.293 32.293 0 17.805 14.484 32.293 32.293 32.293 17.805 0 32.293-14.484 32.293-32.293 0-17.805-14.484-32.293-32.293-32.293zm0 58.332c-14.359 0-26.043-11.684-26.043-26.043s11.684-26.043 26.043-26.043 26.043 11.684 26.043 26.043-11.684 26.043-26.043 26.043zm14.098-31.195c2.8438 2.8438 2.8438 7.4688 0 10.312l-5.3867 5.3867c-0.60938 0.60938-1.4102 0.91406-2.2109 0.91406s-1.5977-0.30469-2.2109-0.91406c-1.2227-1.2227-1.2227-3.1992 0-4.418l3-3h-20.402c-1.7266 0-3.125-1.3984-3.125-3.125s1.3984-3.125 3.125-3.125h20.402l-3-3c-1.2227-1.2227-1.2227-3.1992 0-4.418 1.2227-1.2227 3.1992-1.2227 4.418 0l5.3867 5.3867zm42.277-26.223-25-25c-0.58594-0.58594-1.3789-0.91406-2.2109-0.91406h-33.332c-6.3203 0-11.457 5.1406-11.457 11.457v6.082c0 1.7266 1.3984 3.125 3.125 3.125s3.125-1.3984 3.125-3.125v-6.082c0-2.8711 2.3359-5.207 5.207-5.207h30.207v17.707c0 4.0195 3.2695 7.293 7.293 7.293h17.707v46.875c0 2.8711-2.3359 5.207-5.207 5.207h-16.086c-1.7266 0-3.125 1.3984-3.125 3.125 0 1.7266 1.3984 3.125 3.125 3.125h16.086c6.3203 0 11.457-5.1406 11.457-11.457v-50c0-0.82812-0.32812-1.625-0.91406-2.2109zm-23.043-0.91406c-0.57422 0-1.043-0.46875-1.043-1.043v-13.289l14.332 14.332z"/>

              </svg>

          </button>

  

          ) : null}

        

        </div>

  

  

          {viewId ? <ShareView viewId={viewId} alt="Share View" title="Share with your team" /> : null}

  

          <button className="navicon-button" onClick={() => setIsOpen(true)} alt="Help Tour Button" title="Take a tour of Data Sculpture">

            <svg width="28" height="28" viewBox="0 0 32 33" fill="var(--Grey7)" xmlns="http://www.w3.org/2000/svg">

              <path

                d="M5.73659 2.41016C5.4347 2.41016 5.13281 2.71144 5.13281 3.314V26.5127C5.13281 28.3204 6.64225 29.8268 8.15168 29.8268H25.963C26.2649 29.8268 26.8687 29.5255 26.8687 28.923V24.4037V3.314C26.8687 3.01272 26.5668 2.41016 25.963 2.41016H6.03847H5.73659ZM6.64225 3.91657H25.0573V23.1986H8.15168C7.54791 23.1986 6.94413 23.1986 6.64225 23.4999V3.91657ZM16.0007 5.72426C12.68 5.72426 9.963 8.4358 9.963 11.7499C9.963 15.064 12.68 17.7755 16.0007 17.7755C19.3215 17.7755 22.0385 15.064 22.0385 11.7499C22.0385 8.4358 19.3215 5.72426 16.0007 5.72426ZM16.0007 7.53195C18.4158 7.53195 20.529 9.64093 20.529 12.0512C20.529 14.4614 18.4158 16.5704 16.0007 16.5704C13.5856 16.5704 11.4724 14.4614 11.4724 12.0512C11.4724 9.64093 13.5856 7.53195 16.0007 7.53195ZM16.0007 9.03836C15.397 9.03836 15.0951 9.33964 15.0951 9.94221C15.0951 10.5448 15.397 10.8461 16.0007 10.8461C16.6045 10.8461 16.9064 10.5448 16.9064 9.94221C16.9064 9.33964 16.6045 9.03836 16.0007 9.03836ZM16.0007 11.1473C15.6989 11.1473 15.0951 11.4486 15.0951 12.0512V14.1602C15.0951 14.4614 15.397 15.064 16.0007 15.064C16.3026 15.064 16.9064 14.7627 16.9064 14.1602V12.0512C16.9064 11.7499 16.6045 11.1473 16.0007 11.1473ZM12.68 19.5832C12.3781 19.5832 11.7743 19.8845 12.0762 20.4871C12.0762 20.7884 12.3781 21.0896 12.9819 21.0896H19.3215C19.6234 21.0896 20.2272 20.7884 20.2272 20.1858C20.2272 19.8845 19.9253 19.282 19.3215 19.282H12.9819L12.68 19.5832ZM8.15168 24.705H25.0573V27.7179H8.15168C7.24602 27.7179 6.64225 27.1153 6.64225 26.2114C6.64225 25.3076 7.24602 24.705 8.15168 24.705Z"

                fill="var(--Grey6)"

              />

            </svg>

          </button>


        </nav>
          
        {/* Exporting  */}
        {isExporting && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(20, 20, 20, 0.95)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
              color: "white",
              fontSize: "18px",
            }}
          >
            
            <div style={{ textAlign: "center", width: "100%", maxWidth: "720px", minWidth: "360px" }}>
              <h1 style={{ color: "var(--Grey1)" }}>Exporting your view</h1>
              <div style={{ marginTop: "10px", marginBottom: "36px",fontSize: "14px" }}>Processing data...</div>
              <RecentViewSkeleton count={3}  />
            </div>
          </div>
        )}

        <ExportModal

          isOpen={isExportModalOpen}

          onClose={() => setIsExportModalOpen(false)}

          onExport={handleExport}

          slides={slideData}

        />

      </>

    );

  }

  

  export default NavBar;


